/**
 * Hook for fetching and managing platform cost metrics (Task 080)
 */

import { useState, useEffect, useCallback } from 'react';
import { adminService } from '@/services/admin/HttpAdminAdapter';
import type {
  CostPeriod,
  CostSummary,
  CostTrendsResponse,
  TopCostUsersResponse,
} from '@/services/admin/AdminService';
import Logger from '@/utils/logger';

const logger = Logger.create('useCostMetrics');

export interface UseCostMetricsReturn {
  // Data
  costSummary: CostSummary | null;
  costTrends: CostTrendsResponse | null;
  topUsers: TopCostUsersResponse | null;

  // State
  loading: boolean;
  error: string | null;
  period: CostPeriod;

  // Actions
  setPeriod: (period: CostPeriod) => void;
  refresh: () => void;
}

export function useCostMetrics(
  initialPeriod: CostPeriod = 'month'
): UseCostMetricsReturn {
  const [costSummary, setCostSummary] = useState<CostSummary | null>(null);
  const [costTrends, setCostTrends] = useState<CostTrendsResponse | null>(null);
  const [topUsers, setTopUsers] = useState<TopCostUsersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<CostPeriod>(initialPeriod);

  const fetchCostData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      logger.debug(`Fetching cost metrics for period: ${period}`);

      // Fetch all cost data in parallel
      const [summaryData, trendsData, usersData] = await Promise.all([
        adminService.getCostSummary(period),
        adminService.getCostTrends(30),
        adminService.getTopCostUsers(10, period),
      ]);

      setCostSummary(summaryData);
      setCostTrends(trendsData);
      setTopUsers(usersData);

      logger.debug('Successfully fetched cost metrics');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch cost metrics';
      logger.error('Error fetching cost metrics:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchCostData();
  }, [fetchCostData]);

  const refresh = useCallback(() => {
    fetchCostData();
  }, [fetchCostData]);

  return {
    costSummary,
    costTrends,
    topUsers,
    loading,
    error,
    period,
    setPeriod,
    refresh,
  };
}

export default useCostMetrics;
